import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openOrderWindow } from '../../store/actions/orders';
import { mapOrderWindowData } from '../../services/utilities';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,

  FormControl,
  Select,
  MenuItem,
  InputLabel,
  useMediaQuery,
  Grid
  // TableSortLabel removed -> using custom icons
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  fetchGainersLosers,
  fetchYearlyHigh,
  fetchYearlyLow,
  fetchVolumeShockers,
  fetchMostActiveStocks,
  fetchUpperCircuitBreakers,
  fetchLowerCircuitBreakers,
  fetchReturnOnInvestment,
  fetchDiscountedROI,
  fetchNear52WHigh,
  fetchNear52WLow,
  fetchNearUpperCircuit,
  fetchNearLowerCircuit,
  fetchMostActiveDerivatives,
  fetchGainersLosersDerivatives,
  fetchOIGainersLosers,
  fetchLongUnwinding,
  fetchShortCovering,
  fetchShortBuildUp,
  fetchLongBuildUp,
  fetchDiscountStocks,
  fetchPremiumStocks
} from '../../services/backendAPI';
import DataCard from '../Common/DataCard';

// Equity Tabs
const EQUITY_TABS = [
  { id: 'gainers', label: 'Top Gainers' },
  { id: 'losers', label: 'Top Losers' },
  { id: '52w_high', label: '52 Week High' },
  { id: '52w_low', label: '52 Week Low' },
  { id: 'volume', label: 'Volume Shockers' },
  { id: 'active', label: 'Most Active Volume' },
  { id: 'upper_circuit', label: 'Upp. Circuit Lmt Breakers' },
  { id: 'lower_circuit', label: 'Low. Circuit Lmt Breakers' },
  { id: 'roi', label: 'Return on Investment' },
  { id: 'discounted', label: 'Discounted Stocks' },
  { id: 'near_52w_high', label: 'Near 52W High' },
  { id: 'near_52w_low', label: 'Near 52W Low' },
  { id: 'near_upper_circuit', label: 'Near Upp. Circuit Lmt' },
  { id: 'near_lower_circuit', label: 'Near Low. Circuit Lmt' },
];

// Derivative Tabs
const DERIVATIVE_TABS = [
  { id: 'most_active', label: 'Most Active' },
  { id: 'days_gainers', label: "Day's Gainers" },
  { id: 'days_losers', label: "Day's Losers" },
  // { id: 'fut_oi_gainers', label: 'FUT OI Gainers' },
  // { id: 'fut_oi_losers', label: 'FUT OI Losers' },
  // { id: 'long_unwinding', label: 'Long Unwinding' },
  // { id: 'short_covering', label: 'Short Covering' },
  // { id: 'short_buildup', label: 'Short Build Up' },
  // { id: 'long_buildup', label: 'Long Build Up' },
  // { id: 'discount', label: 'Discount' },
  // { id: 'premium', label: 'Premium' },
];

// NSE Index options
const NSE_INDEX_OPTIONS = [
  { label: 'ALL', value: '' },
  { label: 'NIFTY 50', value: 'nifty_50' },
  { label: 'NIFTY 100', value: 'nifty_100' },
  { label: 'NIFTY 200', value: 'nifty_200' },
  { label: 'NIFTY 500', value: 'nifty_500' },
  { label: 'NIFTY MIDCAP 150', value: 'nifty_midcap_150' },
  { label: 'NIFTY SMALLCAP 250', value: 'nifty_smallcap_250' },
  { label: 'NIFTY AUTO', value: 'nifty_auto' },
  { label: 'NIFTY BANK', value: 'nifty_bank' },
  { label: 'NIFTY ENERGY', value: 'nifty_energy' },
  { label: 'NIFTY FINANCE', value: 'nifty_finance' },
  { label: 'NIFTY FMCG', value: 'nifty_fmcg' },
  { label: 'NIFTY IT', value: 'nifty_it' },
  { label: 'NIFTY METAL', value: 'nifty_metal' },
  { label: 'NIFTY PHARMA', value: 'nifty_pharma' },
  { label: 'NIFTY REALTY', value: 'nifty_realty' },
  { label: 'NIFTY INFRA', value: 'nifty_infra' },
];

// Derivative instrument options
const DERIVATIVE_INSTRUMENT_OPTIONS = [
  { label: 'FUTIDX', value: 'FUTIDX' },
  { label: 'FUTSTK', value: 'FUTSTK' },
  { label: 'OPTIDX', value: 'OPTIDX' },
  { label: 'OPTSTK', value: 'OPTSTK' },
];

const Screeners = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { userSettings } = useSelector((state) => state.profile);

  // Filter states - hardcoded to NSE since no BSE
  const exchange = 'NSE';
  const [segment, setSegment] = useState('Equity');
  const [activeTab, setActiveTab] = useState('gainers');
  const [indexFilter, setIndexFilter] = useState('');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect mobile view
  const [activeStockToken, setActiveStockToken] = useState(null); // Selected stock for mobile actions


  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });


  // Filter drawer state - REMOVED
  // const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  // Get current tabs based on segment
  const currentTabs = segment === 'Equity' ? EQUITY_TABS : DERIVATIVE_TABS;

  // Get current index options based on segment
  const currentIndexOptions = useMemo(() => {
    if (segment === 'Derivative') return DERIVATIVE_INSTRUMENT_OPTIONS;
    return NSE_INDEX_OPTIONS;
  }, [segment]);

  // Reset tab when segment changes
  useEffect(() => {
    setActiveTab(segment === 'Equity' ? 'gainers' : 'most_active');
    setIndexFilter('');
  }, [segment]);

  // API call based on active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      const derivativeExchange = 'NFO';

      // Equity APIs
      if (segment === 'Equity') {
        switch (activeTab) {
          case 'gainers':
            response = await fetchGainersLosers(exchange, indexFilter);
            setData(response?.data?.gainers || []);
            break;
          case 'losers':
            response = await fetchGainersLosers(exchange, indexFilter);
            setData(response?.data?.losers || []);
            break;
          case '52w_high':
            response = await fetchYearlyHigh(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case '52w_low':
            response = await fetchYearlyLow(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'volume':
            response = await fetchVolumeShockers(exchange, indexFilter);
            setData(response?.data?.volume_shockers || []);
            break;
          case 'active':
            response = await fetchMostActiveStocks(exchange, indexFilter);
            setData(response?.data?.most_active_volume || []);
            break;
          case 'upper_circuit':
            response = await fetchUpperCircuitBreakers(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'lower_circuit':
            response = await fetchLowerCircuitBreakers(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'roi':
            response = await fetchReturnOnInvestment(exchange, indexFilter);
            setData(response?.data?.roi || []);
            break;
          case 'discounted':
            response = await fetchDiscountedROI(exchange, indexFilter);
            setData(response?.data?.roi || []);
            break;
          case 'near_52w_high':
            response = await fetchNear52WHigh(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'near_52w_low':
            response = await fetchNear52WLow(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'near_upper_circuit':
            response = await fetchNearUpperCircuit(exchange, indexFilter);
            setData(response?.data || []);
            break;
          case 'near_lower_circuit':
            response = await fetchNearLowerCircuit(exchange, indexFilter);
            setData(response?.data || []);
            break;
          default:
            setData([]);
        }
      } else {
        // Derivative APIs
        const instrumentName = indexFilter || 'FUTIDX';
        switch (activeTab) {
          case 'most_active':
            response = await fetchMostActiveDerivatives(derivativeExchange, instrumentName);
            setData(response?.data?.most_active_volume || []);
            break;
          case 'days_gainers':
            response = await fetchGainersLosersDerivatives(derivativeExchange, instrumentName);
            setData(response?.data?.gainers || []);
            break;
          case 'days_losers':
            response = await fetchGainersLosersDerivatives(derivativeExchange, instrumentName);
            setData(response?.data?.losers || []);
            break;
          case 'fut_oi_gainers':
            response = await fetchOIGainersLosers(derivativeExchange, instrumentName);
            setData(response?.data?.gainers || []);
            break;
          case 'fut_oi_losers':
            response = await fetchOIGainersLosers(derivativeExchange, instrumentName);
            setData(response?.data?.losers || []);
            break;
          case 'long_unwinding':
            response = await fetchLongUnwinding(derivativeExchange, instrumentName);
            setData(response?.data?.long_unwinding || []);
            break;
          case 'short_covering':
            response = await fetchShortCovering(derivativeExchange, instrumentName);
            setData(response?.data?.short_covering || []);
            break;
          case 'short_buildup':
            response = await fetchShortBuildUp(derivativeExchange, instrumentName);
            setData(response?.data?.short_buildup || []);
            break;
          case 'long_buildup':
            response = await fetchLongBuildUp(derivativeExchange, instrumentName);
            setData(response?.data?.long_buildup || []);
            break;
          case 'discount':
            response = await fetchDiscountStocks(derivativeExchange, instrumentName);
            setData(response?.data || []);
            break;
          case 'premium':
            response = await fetchPremiumStocks(derivativeExchange, instrumentName);
            setData(response?.data || []);
            break;
          default:
            setData([]);
        }
      }
    } catch (err) {
      console.error('Error fetching screeners data:', err);
      setError('Failed to fetch screeners data. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [segment, activeTab, indexFilter, exchange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatPrice = (price) => {
    if (price === undefined || price === null) return '—';
    return `₹ ${Number(price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatChange = (change) => {
    if (change === undefined || change === null) return '—';
    return change.toFixed(2);
  };

  const formatPercent = (percent) => {
    if (percent === undefined || percent === null) return '—';
    return `${percent.toFixed(2)}`;
  };

  const formatVolume = (volume) => {
    if (volume === undefined || volume === null) return '0.00';
    return (volume / 1000).toFixed(2);
  };

  const formatTurnover = (turnover) => {
    if (turnover === undefined || turnover === null) return '0.00';
    if (turnover >= 10000000) return `${(turnover / 10000000).toFixed(2)} Cr`;
    if (turnover >= 100000) return `${(turnover / 100000).toFixed(2)} Lac`;
    return turnover.toFixed(2);
  };

  const handleBuy = useCallback((stock, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    dispatch(openOrderWindow(mapOrderWindowData(stock, 'BUY', 'DEFAULT', userSettings?.order_type, userSettings?.product_type)));
  }, [dispatch, userSettings]);

  const handleSell = useCallback((stock, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    dispatch(openOrderWindow(mapOrderWindowData(stock, 'SELL', 'DEFAULT', userSettings?.order_type, userSettings?.product_type)));
  }, [dispatch, userSettings]);

  const handleIndexSelect = (event) => {
    setIndexFilter(event.target.value);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Custom Sort Icon Component
  const CustomSortIcon = ({ active, direction }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
      <ArrowDropUpIcon
        sx={{
          fontSize: 24,
          mb: -1,
          color: active && direction === 'asc' ? 'text.primary' : 'text.disabled',
          opacity: active && direction === 'asc' ? 1 : 0.3
        }}
      />
      <ArrowDropDownIcon
        sx={{
          fontSize: 24,
          mt: -1,
          color: active && direction === 'desc' ? 'text.primary' : 'text.disabled',
          opacity: active && direction === 'desc' ? 1 : 0.3
        }}
      />
    </Box>
  );

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Create a copy to sort
    const sortedArray = [...data];

    if (sortConfig.key) {
      sortedArray.sort((a, b) => {
        let aValue, bValue;

        // Determine values based on key
        switch (sortConfig.key) {
          case 'scripName':
            aValue = a.trading_symbol || a.symbol || a.name || '';
            bValue = b.trading_symbol || b.symbol || b.name || '';
            return sortConfig.direction === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);

          case 'ltp':
            aValue = parseFloat(a.ltp || a.last_price || a.close || 0);
            bValue = parseFloat(b.ltp || b.last_price || b.close || 0);
            break;

          case 'change':
            // Logic for change
            {
              const aLtp = parseFloat(a.ltp || a.last_price || a.close || 0);
              const aClose = parseFloat(a.close_price || a.prev_close || a.previous_close || a.prevClose || 0);
              aValue = aClose === 0 ? 0 : aLtp - aClose;

              const bLtp = parseFloat(b.ltp || b.last_price || b.close || 0);
              const bClose = parseFloat(b.close_price || b.prev_close || b.previous_close || b.prevClose || 0);
              bValue = bClose === 0 ? 0 : bLtp - bClose;
            }
            break;

          case 'percentChange':
            // Logic for % change
            {
              const aLtp = parseFloat(a.ltp || a.last_price || a.close || 0);
              const aClose = parseFloat(a.close_price || a.prev_close || a.previous_close || a.prevClose || 0);
              aValue = aClose === 0 ? 0 : ((aLtp - aClose) / aClose) * 100;

              const bLtp = parseFloat(b.ltp || b.last_price || b.close || 0);
              const bClose = parseFloat(b.close_price || b.prev_close || b.previous_close || b.prevClose || 0);
              bValue = bClose === 0 ? 0 : ((bLtp - bClose) / bClose) * 100;
            }
            break;

          case 'volume':
            aValue = parseFloat(a.tradedQuantity || a.volume || a.traded_volume || 0);
            bValue = parseFloat(b.tradedQuantity || b.volume || b.traded_volume || 0);
            break;

          case 'turnover':
            aValue = parseFloat(a.turnover_value || a.turnover || 0);
            bValue = parseFloat(b.turnover_value || b.turnover || 0);
            break;

          case 'prevClose':
            aValue = parseFloat(a.close_price || a.prev_close || a.previous_close || a.prevClose || 0);
            bValue = parseFloat(b.close_price || b.prev_close || b.previous_close || b.prevClose || 0);
            break;

          default:
            return 0;
        }

        // Numeric sort
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedArray;
  }, [data, sortConfig]);

  const renderTable = () => (
    <TableContainer>
      <Table sx={{ minWidth: isMobile ? '100%' : 1000 }} size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>

            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', py: 1.5, width: '25%', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('scripName')}
              >
                Scrip Name
                <CustomSortIcon active={sortConfig.key === 'scripName'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('ltp')}
              >
                LTP
                <CustomSortIcon active={sortConfig.key === 'ltp'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('change')}
              >
                Day's Change
                <CustomSortIcon active={sortConfig.key === 'change'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('percentChange')}
              >
                Day's %Change
                <CustomSortIcon active={sortConfig.key === 'percentChange'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('volume')}
              >
                Volume(in '1000)
                <CustomSortIcon active={sortConfig.key === 'volume'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('turnover')}
              >
                Turnover Value
                <CustomSortIcon active={sortConfig.key === 'turnover'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleSort('prevClose')}
              >
                Prev Close
                <CustomSortIcon active={sortConfig.key === 'prevClose'} direction={sortConfig.direction} />
              </Box>
            </TableCell>
            {!isMobile && <TableCell align="center" sx={{ fontWeight: 500, color: 'text.secondary', width: 160 }}></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((stock, index) => {
              // Calculate Day's Change and Day's %Change like Capture Web App (headers.jsx)
              const ltp = parseFloat(stock.ltp || stock.last_price || stock.close || 0);
              const closePrice = parseFloat(stock.close_price || stock.prev_close || stock.previous_close || stock.prevClose || 0);
              const dayChange = closePrice === 0 ? 0 : ltp - closePrice;
              const dayPerChange = closePrice === 0 ? 0 : ((ltp - closePrice) / closePrice) * 100;
              const isUp = dayChange >= 0;
              const changeColor = isUp ? theme.palette.success.main : theme.palette.error.main;

              return (
                <TableRow
                  key={stock.instrument_token || stock.token || index}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      '& .action-buttons': { opacity: 1 }
                    },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    cursor: isMobile ? 'pointer' : 'default',
                    backgroundColor: isMobile && (stock.instrument_token || stock.token) === activeStockToken ? theme.palette.action.selected : 'inherit'
                  }}
                  onClick={() => isMobile && setActiveStockToken(stock.instrument_token || stock.token)}
                >
                  {/* Scrip Name - matching Capture Web App exactly */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "medium",
                            fontSize: '0.875rem',
                            color: 'text.primary',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {stock.display_name || stock.trading_symbol || stock.symbol || stock.name || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          {stock.company_name || stock.full_name || stock.scrip_name || ''}
                        </Typography>

                      </Box>
                    </Box>
                  </TableCell>

                  {/* LTP */}
                  <TableCell sx={{ whiteSpace: 'nowrap', position: 'relative' }}>
                    {/* Overlay Action Buttons (Mobile Only) */}
                    {isMobile && (stock.instrument_token || stock.token) === activeStockToken && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 1,
                          backgroundColor: 'transparent',
                          zIndex: 10,
                          px: 1,
                          width: '200%', // Extend width further to cover more columns
                          left: 'auto', // Reset left
                          right: -50, // Shift right more into next column area
                          // backdropFilter removed
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => handleBuy(stock, e)}
                          sx={{
                            backgroundColor: 'success.main',
                            minWidth: '50px',
                            height: '24px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            p: 0.5,
                            boxShadow: 2
                          }}
                        >
                          Buy
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => handleSell(stock, e)}
                          sx={{
                            backgroundColor: 'error.main',
                            minWidth: '50px',
                            height: '24px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            p: 0.5,
                            boxShadow: 2
                          }}
                        >
                          Sell
                        </Button>
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatPrice(stock.ltp || stock.last_price || stock.close)}
                    </Typography>
                  </TableCell>

                  {/* Day's Change */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: changeColor, fontWeight: 500 }}>
                      {formatChange(dayChange)}
                    </Typography>
                  </TableCell>

                  {/* Day's %Change */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: changeColor, fontWeight: 500 }}>
                      {formatPercent(dayPerChange)}
                    </Typography>
                  </TableCell>

                  {/* Volume (in '000) - using tradedQuantity like headers.jsx volumeGainersLosers */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {formatVolume(stock.tradedQuantity || stock.volume || stock.traded_volume)}
                    </Typography>
                  </TableCell>

                  {/* Turnover Value - using turnover_value like headers.jsx */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {formatTurnover(stock.turnover_value || stock.turnover)}
                    </Typography>
                  </TableCell>

                  {/* Prev Close - using close_price like headers.jsx */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {closePrice.toFixed(2)}
                    </Typography>
                  </TableCell>


                  {/* Buy/Sell Buttons + More Options - visible only on hover - (Right for Desktop) */}
                  {!isMobile && (
                    <TableCell align="center">
                      <Box
                        className="action-buttons"
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'center',
                          alignItems: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease-in-out'
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => handleBuy(stock, e)}
                          sx={{
                            backgroundColor: 'success.main',
                            '&:hover': {
                              backgroundColor: 'success.dark',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            },
                            minWidth: '64px',
                            height: '28px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            borderRadius: '14px',
                            transition: 'all 0.2s ease',
                            textTransform: 'none'
                          }}
                        >
                          Buy
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => handleSell(stock, e)}
                          sx={{
                            backgroundColor: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            },
                            minWidth: '64px',
                            height: '28px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            borderRadius: '14px',
                            transition: 'all 0.2s ease',
                            textTransform: 'none'
                          }}
                        >
                          Sell
                        </Button>
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography sx={{ color: 'text.secondary', py: 4 }}>No data available</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const MobileCardView = () => (
    <Box sx={{ p: 1 }}>
      {sortedData.length > 0 ? (
        sortedData.map((stock, index) => {
          const ltp = parseFloat(stock.ltp || stock.last_price || stock.close || 0);
          const closePrice = parseFloat(stock.close_price || stock.prev_close || stock.previous_close || stock.prevClose || 0);
          const dayChange = closePrice === 0 ? 0 : ltp - closePrice;
          const dayPerChange = closePrice === 0 ? 0 : ((ltp - closePrice) / closePrice) * 100;
          const isUp = dayChange >= 0;
          const changeColor = isUp ? theme.palette.success.main : theme.palette.error.main;

          return (
            <DataCard
              key={stock.instrument_token || stock.token || index}
              layout={segment === 'Derivative' ? 'vertical' : 'split'}
              header={{
                label: "SCRIP",
                title: stock.display_name || stock.trading_symbol || stock.symbol || stock.name || '—',
                subtitle: stock.company_name || stock.full_name || stock.scrip_name || ''
              }}
              middleSection={{
                label: "LTP",
                value: formatPrice(ltp),
                color: theme.palette.text.primary
              }}
              status={{
                label: `${isUp ? '+' : ''}${formatChange(dayChange)} (${dayPerChange.toFixed(2)}%)`,
                color: changeColor
              }}
              expandable={true}
              actions={[
                { label: 'Buy', onClick: (e) => handleBuy(stock, e), color: theme.palette.success.main },
                { label: 'Sell', onClick: (e) => handleSell(stock, e), color: theme.palette.error.main }
              ]}
              renderExpandedContent={(
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>Volume(in '1000)</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatVolume(stock.tradedQuantity || stock.volume || stock.traded_volume)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>Turnover Value</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTurnover(stock.turnover_value || stock.turnover)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>Prev Close</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{closePrice.toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              )}
            />
          );
        })
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}>
          <Typography variant="body2">No data available</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {/* Top bar with Segment toggles */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: isMobile ? 1 : 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: isMobile ? 1 : 2
          }}
        >
          {/* Filter Dropdown (Left) - 1/3 on mobile */}
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: isMobile ? 0 : 150,
              flex: isMobile ? 1 : 'none'
            }}
          >
            <InputLabel id="filter-select-label">Index</InputLabel>
            <Select
              labelId="filter-select-label"
              id="filter-select"
              value={indexFilter}
              label="Index"
              onChange={handleIndexSelect}
            >
              {currentIndexOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Equity/Derivative Toggles (Right) - 2/3 on mobile to allow 1/3 each */}
          <ToggleButtonGroup
            value={segment}
            exclusive
            onChange={(e, val) => val && setSegment(val)}
            size="small"
            sx={{
              flex: isMobile ? 2 : 'none',
              display: 'flex',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <ToggleButton
              value="Equity"
              sx={{
                textTransform: 'none', px: 2, py: 0.5,
                flex: isMobile ? 1 : 'none',
                '&.Mui-selected': { backgroundColor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { backgroundColor: theme.palette.success.dark } }
              }}
            >
              Equity
            </ToggleButton>
            <ToggleButton
              value="Derivative"
              sx={{
                textTransform: 'none', px: 2, py: 0.5,
                flex: isMobile ? 1 : 'none',
                '&.Mui-selected': { backgroundColor: theme.palette.grey[700], color: theme.palette.getContrastText(theme.palette.grey[700]), '&:hover': { backgroundColor: theme.palette.grey[800] } }
              }}
            >
              Derivative
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Tabs Row with Left/Right Arrows */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Left Arrow */}
          <IconButton
            size="small"
            onClick={() => {
              const container = document.getElementById('tabs-container');
              if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
            }}
            sx={{
              borderRadius: 0,
              px: 1,
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'transparent', color: 'text.primary' }
            }}
          >
            <Typography variant="body1">&lt;</Typography>
          </IconButton>

          {/* Scrollable Tabs Container */}
          <Box
            id="tabs-container"
            sx={{
              display: 'flex',
              gap: 0,
              flex: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {currentTabs.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  textTransform: 'none', px: 2, py: 1.25, borderRadius: 0,
                  color: activeTab === tab.id ? theme.palette.primary.main : 'text.secondary',
                  borderBottom: activeTab === tab.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  whiteSpace: 'nowrap', flexShrink: 0,
                  '&:hover': { backgroundColor: 'transparent', color: theme.palette.text.primary }
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            size="small"
            onClick={() => {
              const container = document.getElementById('tabs-container');
              if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
            }}
            sx={{
              borderRadius: 0,
              px: 1,
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'transparent', color: 'text.primary' }
            }}
          >
            <Typography variant="body1">&gt;</Typography>
          </IconButton>
        </Box>

        {/* Table Content */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 250px)',
          // Custom Scrollbar Styles
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabledBackground,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.action.disabled,
            },
          },
          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.action.disabledBackground} ${theme.palette.action.hover}`,
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: 'text.secondary', mb: 2 }}>{error}</Typography>
              <Button variant="outlined" onClick={fetchData} startIcon={<RefreshIcon />} size="small">Retry</Button>
            </Box>
          ) : (
            isMobile ? MobileCardView() : renderTable()
          )}
        </Box>

      </Box>

      {/* Filter Drawer (Right Side Panel) */}
      {/* Filter Drawer Removed */}
    </Box>
  );
};

export default Screeners;
